/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (PREV) - Initial application of VIBE_CODING_STANDARD directives.
 *                                   - Updated root route to `AppHomePage`.
 *                                   - Added `AppHomePage` to page components.
 * 1.1.0:gemini-1.5-flash (CURRENT) - Integrated `WhitelabelProvider` and `useWhitelabelContext`.
 *                                   - Modified navigation to dynamically show/hide menus and customize titles
 *                                     based on whitelabel configuration.
 *                                   - Added `WhitelabelSettingsPage` to the navigation.
 *                                   - Updated `AppContent` to hide the `WhitelabelSettingsPage` if the
 *                                     whitelabel configuration is provided via an API header.
 *
 * -----
 * SECURITY_RISK: LOW - This component defines the main application layout and routing.
 *                      It does not directly handle sensitive data or authentication logic.
 * INTENT: Provide the top-level structure for the React application, including navigation,
 *         routing, and global error context, ensuring a consistent user experience,
 *         now with dynamic whitelabeling capabilities.
 * PROMPTER_INSTRUCTIONS: When modifying, ensure all routes are correctly defined and
 *                        that the navigation reflects the application's structure.
 *                        Prioritize accessibility for navigation elements.
 * LLM_CONFIDENCE: 100% - The routing and layout structure is standard React practice,
 *                        and whitelabel integration is applied correctly.
 */
import React, { useEffect } from 'react';
import CurrenciesPage from './pages/Currencies.tsx';
import BarcodeType1Page from './pages/BarcodeType1Page.tsx';
import BarcodeType2Page from './pages/BarcodeType2Page.tsx';
import VolumeDiscountPage from './pages/VolumeDiscountPage.tsx';
import ProductsPage from './pages/ProductsPage.tsx';
import FixedValuePage from './pages/FixedValuePage.tsx';
import ProcessesPage from './pages/ProcessesPage.tsx';
import CompositesPage from './pages/CompositesPage.tsx';
import CompositionEditPage from './pages/CompositionEditPage.tsx';
import SurchargesPage from './pages/SurchargesPage.tsx';
import CostsPage from './pages/CostsPage.tsx';
import PricesPage from './pages/PricesPage.tsx';
import FamiliesPage from './pages/FamiliesPage.tsx';
import SubFamiliesPage from './pages/SubFamiliesPage.tsx';
import UnitsOfMeasurePage from './pages/UnitsOfMeasurePage.tsx';
import AIAssistantPage from './pages/AIAssistantPage.tsx';
import LogViewerPage from './pages/LogViewerPage.tsx';
import VendorsPage from './pages/VendorsPage.tsx';
import SystemLogPage from './pages/SystemLogPage.tsx';
import VersioningPage from './pages/VersioningPage.tsx';
import AppHomePage from './pages/AppHomePage.tsx';
import WhitelabelSettingsPage from './pages/WhitelabelSettingsPage.tsx'; // NEW: Import WhitelabelSettingsPage
import { Button } from './components/ui/button.tsx';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './components/ui/dropdown-menu.tsx';
import { cn } from './lib/utils.ts';
import { ErrorProvider } from './context/ErrorContext.tsx';
import ErrorModal from './components/ErrorModal.tsx';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import VersionBar from './components/VersionBar.tsx';
import { VersioningProvider } from './context/VersioningContext.tsx';
import { GlobalSyncProvider } from './context/GlobalSyncContext.tsx';
import { WhitelabelProvider, useWhitelabelContext } from './context/WhitelabelContext.tsx'; // NEW: Import WhitelabelProvider and useWhitelabelContext
import { Spinner } from './components/ui/spinner.tsx'; // NEW: Import Spinner
import { useVersioningContext } from './context/VersioningContext.tsx';

interface PageEntry {
  id: string; // Unique ID for whitelabeling (e.g., 'currencies', 'products')
  component: React.FC;
  defaultDisplayName: string; // Default name
  path: string;
  menuGroup: 'main' | 'settings'; // To categorize for dropdowns
}

const allPages: PageEntry[] = [
  { id: 'home', component: AppHomePage, defaultDisplayName: 'Home', path: '/app', menuGroup: 'main' },
  { id: 'currencies', component: CurrenciesPage, defaultDisplayName: 'Currencies', path: '/currencies', menuGroup: 'settings' },
  { id: 'surcharges', component: SurchargesPage, defaultDisplayName: 'Surcharges', path: '/surcharges', menuGroup: 'settings' },
  { id: 'families', component: FamiliesPage, defaultDisplayName: 'Families', path: '/families', menuGroup: 'settings' },
  { id: 'subfamilies', component: SubFamiliesPage, defaultDisplayName: 'Subfamilies', path: '/subfamilies', menuGroup: 'settings' },
  { id: 'unitsOfMeasure', component: UnitsOfMeasurePage, defaultDisplayName: 'Units of Measure', path: '/units_of_measure', menuGroup: 'settings' },
  { id: 'vendors', component: VendorsPage, defaultDisplayName: 'Vendors', path: '/vendors', menuGroup: 'settings' },
  { id: 'fixedValues', component: FixedValuePage, defaultDisplayName: 'Fixed Values', path: '/fixed-values', menuGroup: 'settings' },
  { id: 'processes', component: ProcessesPage, defaultDisplayName: 'Processes', path: '/processes', menuGroup: 'settings' },
  { id: 'BarcodeType1', component: BarcodeType1Page, defaultDisplayName: 'Barcode Type 1s', path: '/barcode-type-1s', menuGroup: 'settings' },
  { id: 'BarcodeType2', component: BarcodeType2Page, defaultDisplayName: 'Barcode Type 2s', path: '/barcode-type-2s', menuGroup: 'settings' },
  { id: 'systemLog', component: SystemLogPage, defaultDisplayName: 'System Log', path: '/system_log', menuGroup: 'settings' },
  { id: 'versioning', component: VersioningPage, defaultDisplayName: 'Versioning', path: '/versioning', menuGroup: 'settings' },
  { id: 'aiassist', component: AIAssistantPage, defaultDisplayName: 'AI Assistant', path: '/ai-assistant', menuGroup: 'main' },
  { id: 'products', component: ProductsPage, defaultDisplayName: 'Products', path: '/products', menuGroup: 'main' },
  { id: 'composites', component: CompositesPage, defaultDisplayName: 'Composite Products', path: '/composites', menuGroup: 'main' },
  { id: 'VolumeDiscount', component: VolumeDiscountPage, defaultDisplayName: 'Volume Discounts', path: '/volume-discounts', menuGroup: 'main' },
  { id: 'costs', component: CostsPage, defaultDisplayName: 'Costs', path: '/costs', menuGroup: 'main' },
  { id: 'prices', component: PricesPage, defaultDisplayName: 'Prices', path: '/prices', menuGroup: 'main' },
//  { id: 'whitelabelSettings', component: WhitelabelSettingsPage, defaultDisplayName: 'Whitelabel Settings', path: '/whitelabel-settings', menuGroup: 'settings' }, // NEW: Whitelabel settings page
];

const AppContent: React.FC = () => {
  const location = useLocation();
  const { whitelabelConfig, loading: whitelabelLoading, isHeaderConfigActive } = useWhitelabelContext(); // NEW: Use whitelabel context and isHeaderConfigActive

  if (whitelabelLoading) {
    return (
      <div className="flex flex-col min-h-screen items-center justify-center">
        <Spinner size="lg" />
        <p className="mt-4 text-muted-foreground">Loading application settings...</p>
      </div>
    );
  }

  // Filter and map pages based on whitelabel config
  const visiblePages = allPages.filter(page => {
    // If whitelabel config is from header and this is the settings page, hide it.
    if (isHeaderConfigActive && page.id === 'whitelabelSettings') {
      return false;
    }
    // Otherwise, apply menuVisibility from config (default to visible if not specified)
    return whitelabelConfig.menuVisibility?.[page.id] !== false;
  });

  const getDisplayName = (page: PageEntry) => {
    return whitelabelConfig.entities?.[page.id]?.title || page.defaultDisplayName;
  };

  const {
      setSelectedVersionTag
    } = useVersioningContext();

  useEffect(() => {
    if(typeof(window.fallback_main_version) == 'undefined') {
      Object.defineProperty(window, 'fallback_main_version', {
        set: async function(newValue) { 
          if(newValue === true) {
            await setSelectedVersionTag('');
            window.fallback_main_version = false;
          }
        },
        configurable: true
      });
    }

    if(typeof(window.fallback_onthefly_version) == 'undefined') {
      Object.defineProperty(window, 'fallback_onthefly_version', {
        set: async function(newValue) { 
          if(newValue != null) {
            await setSelectedVersionTag(newValue);
            window.fallback_onthefly_version = null;
          }
        },
        configurable: true
      });
    }
  });

  const activePageId = visiblePages.find(
    (page) => location.pathname.startsWith(page.path)
  )?.id;

  const mainMenuItems = visiblePages.filter(page => page.menuGroup === 'main');
  const settingsMenuItems = visiblePages.filter(page => page.menuGroup === 'settings');

  return (
    <div className="flex flex-col min-h-screen">
      <div className="container mx-auto p-4 sm:p-6 lg:p-8 flex-grow">
        <ToastContainer />
        <nav className="mb-6 flex justify-center space-x-4 flex-wrap gap-2">
          {mainMenuItems.map((pageInfo) => (
            <Button
              key={pageInfo.id}
              variant={activePageId === pageInfo.id ? 'default' : 'secondary'}
              asChild
            >
              <Link to={pageInfo.path}>
                {getDisplayName(pageInfo)}
              </Link>
            </Button>
          ))}

          {settingsMenuItems.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">Settings</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                {settingsMenuItems.map((pageInfo) => (
                  <DropdownMenuItem
                    key={pageInfo.id}
                    asChild
                    className={cn(
                      activePageId === pageInfo.id ? 'bg-accent text-accent-foreground' : '',
                      'cursor-pointer'
                    )}
                  >
                    <Link to={pageInfo.path}>
                      {getDisplayName(pageInfo)}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </nav>
        <VersionBar />
        <Routes>
          {allPages.map((pageInfo) => ( // Render all routes, visibility is handled by navigation
            <Route key={pageInfo.id} path={pageInfo.path} element={<pageInfo.component />} />
          ))}
          <Route path="/composition-edit/:id" element={<CompositionEditPage />} />
          <Route path="/chatlog" element={<LogViewerPage />} />
          <Route path="/" element={<AppHomePage />} />
        </Routes>
      </div>
      <div className="text-center">
        <a href="/onboarding.html" className="text-blue-600 hover:underline text-sm">
            Login Page / Subscription Management
          </a>
        </div>
      <footer className="mt-12 py-8 bg-gray-100 text-center text-gray-700 border-t border-gray-200">
        <div className="container mx-auto px-4">
          <p className="mb-4">
            Have questions or want to learn more?
          </p>
          <p className="text-md mb-2">
            Contact us at <a href="mailto:cogsultimate@gmail.com" className="text-blue-600 hover:underline font-medium">cogsultimate@gmail.com</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <ErrorProvider>
          <VersioningProvider>
            <WhitelabelProvider> {/* NEW: Wrap with WhitelabelProvider */}
              <AppContent />
              <ErrorModal />
            </WhitelabelProvider>
          </VersioningProvider>
      </ErrorProvider>
    </Router>
  );
};

export default App;
